/* Styled dropdown for the subscription plan `<select>`.
 *
 * The widget's delivery-frequency control is a native `<select>`, so its open
 * panel is drawn by the operating system and cannot be styled — the closed
 * control matched the design but the list did not.
 *
 * WHY THE NATIVE ELEMENT STAYS
 * `subscription-widget.js` drives price updates, the URL parameter and the
 * sticky price block from `select.options`, `select.selectedIndex` and each
 * option's `dataset` (subscription-widget.js:79-99). Replacing the element with
 * a list of `<li>`s would break all of that, and the `<select>` is also a real
 * form control submitted with the product form.
 *
 * So the native element is kept and hidden, and this draws a panel beside it.
 * Choosing an option writes `select.value` and dispatches a `change` event, so
 * the existing listener runs exactly as it does for a native selection — this
 * element never touches prices itself.
 *
 * ACCESSIBILITY
 * The trigger is a real `<button>` with `aria-expanded` and `aria-controls`;
 * the panel is a `listbox` of `option`s with `aria-selected`. Arrow keys, Home,
 * End, Enter, Space and Escape are handled, and focus returns to the trigger on
 * close. The native `<select>` keeps its `<label>` and stays in the accessibility
 * tree for assistive tech that prefers it — it is hidden from sight, not from
 * screen readers.
 */

if (!customElements.get("subscription-plan-select")) {
  customElements.define(
    "subscription-plan-select",
    class SubscriptionPlanSelect extends HTMLElement {
      connectedCallback() {
        this.select = this.querySelector("select");
        if (!this.select || this.dataset.ready === "true") return;

        this.build();
        this.bind();
        this.dataset.ready = "true";
      }

      build() {
        const labelId = `${this.select.id}-label`;

        this.trigger = document.createElement("button");
        this.trigger.type = "button";
        this.trigger.className = "wt-plan-select__trigger";
        this.trigger.setAttribute("aria-haspopup", "listbox");
        this.trigger.setAttribute("aria-expanded", "false");
        this.trigger.id = labelId;

        this.label = document.createElement("span");
        this.label.className = "wt-plan-select__label";
        this.trigger.append(this.label);

        this.panel = document.createElement("ul");
        this.panel.className = "wt-plan-select__panel";
        this.panel.setAttribute("role", "listbox");
        this.panel.setAttribute("aria-labelledby", labelId);
        this.panel.hidden = true;

        Array.from(this.select.options).forEach((option, index) => {
          const item = document.createElement("li");
          item.className = "wt-plan-select__option";
          item.setAttribute("role", "option");
          item.dataset.index = String(index);
          item.tabIndex = -1;
          item.textContent = option.textContent.trim();
          this.panel.append(item);
        });

        this.trigger.setAttribute("aria-controls", this.panel.id || "");
        this.append(this.trigger, this.panel);
        this.sync();
      }

      /* Mirrors the native element's current selection into the panel. Called on
         build and after any change — including one made by other scripts, such
         as a variant switch re-rendering the widget. */
      sync() {
        const selected = this.select.selectedIndex;
        this.label.textContent =
          this.select.options[selected]?.textContent.trim() ?? "";
        this.panel.querySelectorAll(".wt-plan-select__option").forEach((li) => {
          const isSelected = Number(li.dataset.index) === selected;
          li.setAttribute("aria-selected", isSelected ? "true" : "false");
          li.classList.toggle("is-selected", isSelected);
        });
      }

      bind() {
        this.trigger.addEventListener("click", () => this.toggle());

        this.panel.addEventListener("click", (event) => {
          const item = event.target.closest(".wt-plan-select__option");
          if (item) this.choose(Number(item.dataset.index));
        });

        this.trigger.addEventListener("keydown", (event) => {
          if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
            event.preventDefault();
            this.open();
          }
        });

        this.panel.addEventListener("keydown", (event) => this.onPanelKey(event));

        // Pointer-down rather than click: a click that closes the panel should
        // not also activate whatever sits underneath it.
        this.onOutside = (event) => {
          if (!this.contains(event.target)) this.close();
        };

        // The widget re-renders on variant change, and other scripts may set the
        // value directly, so the panel follows the native element rather than
        // assuming it is the only thing writing to it.
        this.select.addEventListener("change", () => this.sync());
      }

      onPanelKey(event) {
        const items = Array.from(
          this.panel.querySelectorAll(".wt-plan-select__option"),
        );
        const current = items.indexOf(document.activeElement);

        switch (event.key) {
          case "ArrowDown":
            event.preventDefault();
            items[Math.min(current + 1, items.length - 1)]?.focus();
            break;
          case "ArrowUp":
            event.preventDefault();
            items[Math.max(current - 1, 0)]?.focus();
            break;
          case "Home":
            event.preventDefault();
            items[0]?.focus();
            break;
          case "End":
            event.preventDefault();
            items[items.length - 1]?.focus();
            break;
          case "Enter":
          case " ":
            event.preventDefault();
            if (current > -1) this.choose(current);
            break;
          case "Escape":
            event.preventDefault();
            this.close();
            break;
          case "Tab":
            this.close();
            break;
        }
      }

      /* Writes through the native element. `change` is dispatched so
         subscription-widget.js updates the price exactly as it would for a
         native selection — this element deliberately knows nothing about
         prices. */
      choose(index) {
        if (this.select.selectedIndex !== index) {
          this.select.selectedIndex = index;
          this.select.dispatchEvent(new Event("change", { bubbles: true }));
        }
        this.sync();
        this.close();
        this.trigger.focus();
      }

      toggle() {
        this.panel.hidden ? this.open() : this.close();
      }

      open() {
        this.panel.hidden = false;
        this.trigger.setAttribute("aria-expanded", "true");
        this.classList.add("is-open");
        document.addEventListener("pointerdown", this.onOutside);
        const selected =
          this.panel.querySelector(".is-selected") ||
          this.panel.querySelector(".wt-plan-select__option");
        selected?.focus();
      }

      close() {
        if (this.panel.hidden) return;
        this.panel.hidden = true;
        this.trigger.setAttribute("aria-expanded", "false");
        this.classList.remove("is-open");
        document.removeEventListener("pointerdown", this.onOutside);
      }

      disconnectedCallback() {
        document.removeEventListener("pointerdown", this.onOutside);
      }
    },
  );
}
